<script setup>
import { ref, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';

import Button from 'primevue/button';
import Card from 'primevue/card';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import Badge from 'primevue/badge';
import Chip from 'primevue/chip';
import Divider from 'primevue/divider';
import ConfirmDialog from 'primevue/confirmdialog';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const confirm = useConfirm();

const props = defineProps({
    title: String,
    offers: Array,
    coins: Object,
});

// Dialog for offer details
const showModal = ref(false);
const selectedOffer = ref(null);

const showOffer = (offer) => {
    selectedOffer.value = offer;
    showModal.value = true;
};

const accept = (offer) => {
    router.post(route('offers.accept', offer.id), null, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            showModal.value = false;
            setTimeout(() => selectedOffer.value = null, 250);
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Offer accepted successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to accept offer',
                life: 5000
            });
        }
    });
};

const reject = (offer) => {
    router.delete(route('offers.reject', offer.id), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            showModal.value = false;
            setTimeout(() => selectedOffer.value = null, 250);
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Offer rejected successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to reject offer',
                life: 5000
            });
        }
    });
};

const confirmAccept = (offer) => {
    confirm.require({
        message: $t('Are you sure you want to accept this offer?'),
        header: $t('Confirm Accept'),
        icon: 'pi pi-check-circle',
        accept: () => accept(offer),
        acceptClass: 'p-button-success'
    });
};

const confirmReject = (offer) => {
    confirm.require({
        message: $t('Are you sure you want to reject this offer?'),
        header: $t('Confirm Reject'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => reject(offer),
        acceptClass: 'p-button-danger'
    });
};

const formatCurrency = (value, currency) => {
    if (!value || !currency) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
    }).format(value);
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
};

const getStatusSeverity = (status) => {
    switch (status) {
        case 'PENDING': return 'warn';
        case 'ACCEPTED': return 'success';
        case 'DISABLED': return 'danger';
        default: return 'info';
    }
};
</script>

<template>
    <AdminMainLayout>
        <!-- Header -->
        <div class="flex align-items-center justify-content-between mb-4">
            <div>
                <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ $t('Site Offers Management') }}</h1>
                <p class="text-gray-600">{{ $t('Review and manage pricing table offers from sellers') }}</p>
            </div>
        </div>

        <!-- Statistics Cards -->
        <div class="grid mb-4">
            <div class="col-12 md:col-4">
                <Card>
                    <template #content>
                        <div class="flex align-items-center gap-3">
                            <div class="flex-1">
                                <div class="text-2xl font-bold">{{ offers.filter(o => o.status === 'PENDING').length }}</div>
                                <div class="text-gray-600">{{ $t('Pending Offers') }}</div>
                            </div>
                            <i class="pi pi-clock text-orange-500 text-3xl"></i>
                        </div>
                    </template>
                </Card>
            </div>

            <div class="col-12 md:col-4">
                <Card>
                    <template #content>
                        <div class="flex align-items-center gap-3">
                            <div class="flex-1">
                                <div class="text-2xl font-bold">{{ offers.filter(o => o.status === 'ACCEPTED').length }}</div>
                                <div class="text-gray-600">{{ $t('Accepted Offers') }}</div>
                            </div>
                            <i class="pi pi-check-circle text-green-500 text-3xl"></i>
                        </div>
                    </template>
                </Card>
            </div>

            <div class="col-12 md:col-4">
                <Card>
                    <template #content>
                        <div class="flex align-items-center gap-3">
                            <div class="flex-1">
                                <div class="text-2xl font-bold">{{ offers.filter(o => o.status === 'DISABLED').length }}</div>
                                <div class="text-gray-600">{{ $t('Rejected Offers') }}</div>
                            </div>
                            <i class="pi pi-times-circle text-red-500 text-3xl"></i>
                        </div>
                    </template>
                </Card>
            </div>
        </div>

        <!-- Offers Table -->
        <Card>
            <template #content>
                <DataTable 
                    :value="offers" 
                    dataKey="id"
                    paginator 
                    :rows="25"
                    :rowsPerPageOptions="[10, 25, 50]"
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    :currentPageReportTemplate="`${$t('Showing')} {first} ${$t('to')} {last} ${$t('of')} {totalRecords} ${$t('offers')}`"
                    responsiveLayout="scroll"
                    class="p-datatable-sm">
                    
                    <template #header>
                        <div class="flex align-items-center justify-content-between">
                            <h2 class="text-xl font-semibold">{{ $t('Offers List') }}</h2>
                        </div>
                    </template>

                    <!-- Site Column -->
                    <Column field="site.url" :header="$t('Site')" sortable style="min-width: 200px">
                        <template #body="slotProps">
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-globe text-gray-500"></i>
                                <div>
                                    <div class="font-medium">
                                        {{ slotProps.data.site?.url?.replace(/^https?:\/\/(www\.)?/, '') || 'N/A' }}
                                    </div>
                                    <div v-if="slotProps.data.site?.category" class="text-xs text-gray-500">
                                        {{ slotProps.data.site.category.name }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Column>

                    <!-- Seller Column -->
                    <Column field="seller.name" :header="$t('Seller')" sortable style="min-width: 150px">
                        <template #body="slotProps">
                            <div class="flex align-items-center gap-2">
                                <i class="pi pi-user text-gray-500"></i>
                                <div>
                                    <div class="font-medium">{{ slotProps.data.seller?.name || 'N/A' }}</div>
                                    <div v-if="slotProps.data.seller?.email" class="text-xs text-gray-500">
                                        {{ slotProps.data.seller.email }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Column>

                    <!-- Status Column -->
                    <Column field="status" :header="$t('Status')" sortable style="width: 120px">
                        <template #body="slotProps">
                            <Tag 
                                :value="slotProps.data.status" 
                                :severity="getStatusSeverity(slotProps.data.status)" />
                        </template>
                    </Column>

                    <!-- Types Count -->
                    <Column :header="$t('Types')" style="width: 100px">
                        <template #body="slotProps">
                            <Badge :value="slotProps.data.types?.length || 0" severity="info" />
                        </template>
                    </Column>

                    <!-- Date Column -->
                    <Column field="created_at" :header="$t('Created')" sortable style="width: 120px">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.created_at) }}
                        </template>
                    </Column>

                    <!-- Actions Column -->
                    <Column :header="$t('Actions')" style="width: 180px" :exportable="false">
                        <template #body="slotProps">
                            <div class="flex gap-1">
                                <Button 
                                    icon="pi pi-eye" 
                                    size="small" 
                                    severity="info"
                                    v-tooltip="$t('View Details')"
                                    @click="showOffer(slotProps.data)" />
                                
                                <Button 
                                    v-if="slotProps.data.status === 'PENDING'"
                                    icon="pi pi-check" 
                                    size="small" 
                                    severity="success"
                                    v-tooltip="$t('Accept')"
                                    @click="confirmAccept(slotProps.data)" />
                                
                                <Button 
                                    v-if="slotProps.data.status === 'PENDING'"
                                    icon="pi pi-times" 
                                    size="small" 
                                    severity="danger"
                                    v-tooltip="$t('Reject')"
                                    @click="confirmReject(slotProps.data)" />
                            </div>
                        </template>
                    </Column>

                    <template #empty>
                        <div class="text-center p-4">
                            <i class="pi pi-inbox text-4xl text-gray-400 mb-3"></i>
                            <p class="text-gray-600 text-lg">{{ $t('No offers found') }}</p>
                            <p class="text-gray-500">{{ $t('There are no site offers to display') }}</p>
                        </div>
                    </template>
                </DataTable>
            </template>
        </Card>

        <!-- Offer Details Dialog -->
        <Dialog 
            v-model:visible="showModal" 
            modal 
            :header="$t('Offer Details')" 
            :style="{width: '800px'}"
            class="p-fluid">
            
            <div v-if="selectedOffer">
                <!-- Site Information -->
                <Card class="mb-4">
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-globe"></i>
                            {{ $t('Site Information') }}
                        </div>
                    </template>
                    <template #content>
                        <div class="grid">
                            <div class="col-12 md:col-6">
                                <strong>{{ $t('URL') }}:</strong> 
                                <a :href="selectedOffer.site?.url" target="_blank" class="text-primary ml-2">
                                    {{ selectedOffer.site?.url }}
                                </a>
                            </div>
                            <div class="col-12 md:col-6">
                                <strong>{{ $t('Category') }}:</strong> 
                                <span class="ml-2">{{ selectedOffer.site?.category?.name || 'N/A' }}</span>
                            </div>
                            <div class="col-12 md:col-4">
                                <strong>{{ $t('DA') }}:</strong> 
                                <span class="ml-2">{{ selectedOffer.site?.da || 'N/A' }}</span>
                            </div>
                            <div class="col-12 md:col-4">
                                <strong>{{ $t('DR') }}:</strong> 
                                <span class="ml-2">{{ selectedOffer.site?.dr || 'N/A' }}</span>
                            </div>
                            <div class="col-12 md:col-4">
                                <strong>{{ $t('Traffic') }}:</strong> 
                                <span class="ml-2">{{ selectedOffer.site?.traffic || 'N/A' }}</span>
                            </div>
                        </div>
                    </template>
                </Card>

                <!-- Seller Information -->
                <Card class="mb-4">
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-user"></i>
                            {{ $t('Seller Information') }}
                        </div>
                    </template>
                    <template #content>
                        <div class="grid">
                            <div class="col-12 md:col-6">
                                <strong>{{ $t('Name') }}:</strong> 
                                <span class="ml-2">{{ selectedOffer.seller?.name || 'N/A' }}</span>
                            </div>
                            <div class="col-12 md:col-6">
                                <strong>{{ $t('Email') }}:</strong> 
                                <span class="ml-2">{{ selectedOffer.seller?.email || 'N/A' }}</span>
                            </div>
                        </div>
                    </template>
                </Card>

                <!-- Pricing Details -->
                <Card class="mb-4">
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-dollar"></i>
                            {{ $t('Pricing Details') }}
                        </div>
                    </template>
                    <template #content>
                        <!-- Default Type -->
                        <div v-if="selectedOffer.default" class="mb-4">
                            <h4>{{ $t('Default') }}</h4>
                            <div class="grid">
                                <div class="col-6">
                                    <strong>{{ $t('Cost') }}:</strong> 
                                    <span class="ml-2">{{ formatCurrency(selectedOffer.default.pivot?.cost, selectedOffer.default.pivot?.cost_coin) }}</span>
                                </div>
                                <div class="col-6">
                                    <strong>{{ $t('Sale') }}:</strong> 
                                    <span class="ml-2">{{ formatCurrency(selectedOffer.default.pivot?.sale, selectedOffer.default.pivot?.sale_coin) }}</span>
                                </div>
                            </div>
                        </div>

                        <Divider />

                        <!-- Types -->
                        <div v-if="selectedOffer.types && selectedOffer.types.length > 0">
                            <h4>{{ $t('Additional Types') }}</h4>
                            <div v-for="type in selectedOffer.types" :key="type.id" class="mb-3 p-3 border-1 surface-border border-round">
                                <div class="flex align-items-center justify-content-between mb-2">
                                    <strong>{{ type.name }}</strong>
                                    <Chip v-if="type.pivot?.available" :label="$t('Available')" class="p-chip-success" />
                                </div>
                                <div class="grid">
                                    <div class="col-6">
                                        <strong>{{ $t('Cost') }}:</strong> 
                                        <span class="ml-2">{{ formatCurrency(type.pivot?.cost, type.pivot?.cost_coin) }}</span>
                                    </div>
                                    <div class="col-6">
                                        <strong>{{ $t('Sale') }}:</strong> 
                                        <span class="ml-2">{{ formatCurrency(type.pivot?.sale, type.pivot?.sale_coin) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Card>

                <!-- Offer Status and Actions -->
                <Card>
                    <template #content>
                        <div class="flex align-items-center justify-content-between">
                            <div class="flex align-items-center gap-3">
                                <strong>{{ $t('Status') }}:</strong>
                                <Tag 
                                    :value="selectedOffer.status" 
                                    :severity="getStatusSeverity(selectedOffer.status)" />
                            </div>
                            
                            <div v-if="selectedOffer.status === 'PENDING'" class="flex gap-2">
                                <Button 
                                    :label="$t('Accept')" 
                                    icon="pi pi-check" 
                                    severity="success"
                                    @click="confirmAccept(selectedOffer)" />
                                <Button 
                                    :label="$t('Reject')" 
                                    icon="pi pi-times" 
                                    severity="danger"
                                    @click="confirmReject(selectedOffer)" />
                            </div>
                        </div>
                    </template>
                </Card>
            </div>

            <template #footer>
                <Button 
                    :label="$t('Close')" 
                    icon="pi pi-times" 
                    severity="secondary" 
                    @click="showModal = false; selectedOffer = null" />
            </template>
        </Dialog>

        <ConfirmDialog />
    </AdminMainLayout>
</template>

<style scoped>
.p-chip-success {
    background: var(--green-100);
    color: var(--green-800);
}
</style>